import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const MutualFunds = () => {
  // SIP Calculator State
  const [monthlyInvestment, setMonthlyInvestment] = useState(10000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);

  // Calculation Logic
  const totalMonths = timePeriod * 12;
  const monthlyRate = expectedReturn / 12 / 100;
  const investedAmount = monthlyInvestment * totalMonths;
  const estimatedWealth = Math.round(
    monthlyInvestment *
      ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) *
      (1 + monthlyRate)
  );
  const totalGains = estimatedWealth - investedAmount;

  const fundCategories = [
    {
      id: 'equity',
      title: 'Equity & Growth Funds',
      desc: 'Ideal for long-term compounding, capital appreciation, and beating inflation with high-growth Indian enterprises.',
      badge: 'High Growth',
      horizon: '5+ Years',
      risk: 'Moderately High',
      suitableFor: 'Long-term wealth creation, retirement, and milestone goals.'
    },
    {
      id: 'index',
      title: 'Passive Index & ETF Funds',
      desc: 'Low-cost, high-transparency investing tracking the Nifty 50 and Nifty Next 50 with disciplined indexing.',
      badge: 'Low Cost / Consistent',
      horizon: '3+ Years',
      risk: 'Moderate',
      suitableFor: 'Core portfolio stability and steady compounding.'
    },
    {
      id: 'hybrid',
      title: 'Balanced Advantage & Hybrid Funds',
      desc: 'Dynamically manages exposure between equity and debt to preserve capital during market pullbacks.',
      badge: 'Balanced Shield',
      horizon: '3 - 5 Years',
      risk: 'Moderate',
      suitableFor: 'Risk-averse investors seeking growth with downside cushion.'
    },
    {
      id: 'debt',
      title: 'Debt & Liquid Reserves',
      desc: 'Provides steady, predictable yields and liquid treasury management alternative to traditional deposits.',
      badge: 'Capital Protection',
      horizon: '1 - 3 Years',
      risk: 'Low',
      suitableFor: 'Emergency funds, short-term deployment, and steady income.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#07131F] text-slate-100 selection:bg-[#16325B] selection:text-white">
      {/* 1. HERO SECTION */}
      <section className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 border-b border-slate-800/80 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(#3B82F6_1px,transparent_1px)] [background-size:24px_24px]"></div>
        
        <div className="max-w-6xl mx-auto relative z-10 text-center">
          {/* <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#16325B]/40 border border-blue-500/30 text-[#60A5FA] text-xs sm:text-sm font-medium mb-6">
            <span className="w-2 h-2 rounded-full bg-[#60A5FA] animate-pulse"></span>
            SEBI / NISM-Compliant Advisory & Distribution Architecture
          </div> */}

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white tracking-tight leading-tight max-w-4xl mx-auto">
            Disciplined Wealth Creation with <span className="text-[#60A5FA]">Curated Mutual Funds</span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto font-sans leading-relaxed">
            Move beyond random speculation. Build resilient, inflation-beating portfolios aligned with your risk appetite, horizon, and life milestones.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/apply"
              className="px-8 py-3.5 rounded-xl bg-[#16325B] hover:bg-[#1D4ED8] text-white font-bold transition-all duration-300 shadow-lg shadow-[#16325B]/40 border border-blue-400/20"
            >
              Start Your SIP With Welvate
            </Link>
            <a
              href="#calculator"
              className="px-8 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold border border-slate-700 transition-all duration-300"
            >
              Explore SIP Calculator
            </a>
          </div>
        </div>
      </section>

      {/* 2. VALUE PROPOSITION STATS */}
      <section className="py-12 bg-[#091B2C] border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div className="p-4">
            <div className="text-3xl sm:text-4xl font-bold text-[#60A5FA] font-serif">100%</div>
            <div className="text-sm text-slate-300 mt-1">Research-Backed Allocation</div>
          </div>
          <div className="p-4">
            <div className="text-3xl sm:text-4xl font-bold text-white font-serif">Zero</div>
            <div className="text-sm text-slate-300 mt-1">Hidden Markups</div>
          </div>
          <div className="p-4">
            <div className="text-3xl sm:text-4xl font-bold text-[#60A5FA] font-serif">Periodic</div>
            <div className="text-sm text-slate-300 mt-1">Portfolio Rebalancing</div>
          </div>
          <div className="p-4">
            <div className="text-3xl sm:text-4xl font-bold text-white font-serif">Personalized</div>
            <div className="text-sm text-slate-300 mt-1">Goal Alignment</div>
          </div>
        </div>
      </section>

      {/* 3. SIP CALCULATOR SECTION */}
      <section id="calculator" className="py-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white">
            Visualize the Power of <span className="text-[#60A5FA]">Compounding</span>
          </h2>
          <p className="mt-3 text-slate-300">
            See how small, disciplined monthly investments compound into generational capital over time.
          </p>
        </div>

        <div className="bg-[#0B1E30] rounded-2xl border border-slate-700/80 p-6 sm:p-10 shadow-2xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Sliders */}
          <div className="lg:col-span-7 space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-slate-300">Monthly Investment</label>
                <span className="text-lg font-bold text-[#60A5FA] font-mono">
                  ₹{monthlyInvestment.toLocaleString('en-IN')}
                </span>
              </div>
              <input
                type="range"
                min="1000"
                max="200000"
                step="1000"
                value={monthlyInvestment}
                onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                className="w-full accent-[#3B82F6] bg-slate-700 h-2 rounded-lg cursor-pointer"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-slate-300">Expected Annual Return (CAGR)</label>
                <span className="text-lg font-bold text-[#60A5FA] font-mono">{expectedReturn}%</span>
              </div>
              <input
                type="range"
                min="6"
                max="24"
                step="0.5"
                value={expectedReturn}
                onChange={(e) => setExpectedReturn(Number(e.target.value))}
                className="w-full accent-[#3B82F6] bg-slate-700 h-2 rounded-lg cursor-pointer"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-slate-300">Time Horizon</label>
                <span className="text-lg font-bold text-[#60A5FA] font-mono">{timePeriod} Years</span>
              </div>
              <input
                type="range"
                min="1"
                max="30"
                step="1"
                value={timePeriod}
                onChange={(e) => setTimePeriod(Number(e.target.value))}
                className="w-full accent-[#3B82F6] bg-slate-700 h-2 rounded-lg cursor-pointer"
              />
            </div>
          </div>

          {/* Results Summary Card */}
          <div className="lg:col-span-5 bg-[#081523] rounded-xl p-6 border border-slate-700 space-y-4">
            <div>
              <span className="text-xs text-slate-300 uppercase tracking-wider">Invested Amount</span>
              <div className="text-xl font-bold text-slate-200 font-mono">
                ₹{investedAmount.toLocaleString('en-IN')}
              </div>
            </div>

            <div>
              <span className="text-xs text-slate-300 uppercase tracking-wider">Estimated Gains</span>
              <div className="text-xl font-bold text-[#60A5FA] font-mono">
                +₹{totalGains.toLocaleString('en-IN')}
              </div>
            </div>

            <hr className="border-slate-800" />

            <div>
              <span className="text-xs text-slate-300 uppercase tracking-wider">Total Expected Corpus</span>
              <div className="text-3xl font-extrabold text-white font-mono mt-1">
                ₹{estimatedWealth.toLocaleString('en-IN')}
              </div>
            </div>

            <Link
              to="/apply"
              className="w-full block text-center py-3 bg-[#16325B] hover:bg-[#1D4ED8] text-white font-bold rounded-lg transition-all shadow-md border border-blue-400/20"
            >
              Build This Portfolio
            </Link>
          </div>
        </div>
      </section>

      {/* 4. FUND CATEGORIES */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-serif font-bold text-white">
            Curated Categories for Every Goal
          </h2>
          <p className="mt-2 text-slate-300">
            Tailored allocations depending on capital preservation vs. high-alpha targets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {fundCategories.map((cat) => (
            <div
              key={cat.id}
              className="p-6 rounded-xl bg-[#0B1E30] border border-slate-700/70 hover:border-blue-500/50 transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-bold text-white">{cat.title}</h3>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#16325B]/40 text-[#60A5FA] border border-blue-500/30">
                    {cat.badge}
                  </span>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed mb-4">{cat.desc}</p>
                <div className="text-xs text-slate-300 space-y-1 mb-4">
                  <div><span className="text-slate-200 font-semibold">Horizon:</span> {cat.horizon}</div>
                  <div><span className="text-slate-200 font-semibold">Risk Profile:</span> {cat.risk}</div>
                  <div><span className="text-slate-200 font-semibold">Best Suited For:</span> {cat.suitableFor}</div>
                </div>
              </div>

              <Link
                to="/contact"
                className="text-sm font-semibold text-[#60A5FA] hover:text-blue-300 inline-flex items-center gap-1.5 mt-2"
              >
                Inquire Allocation Strategy &rarr;
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* 5. FOOTER CTA */}
      <section className="py-16 border-t border-slate-800 bg-[#091B2C] text-center px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-serif font-bold text-white">
            Ready to structure your investment portfolio?
          </h2>
          <p className="mt-3 text-slate-300">
            Connect with our advisory desk for personalized fund recommendations, portfolio health-checks, and tax-efficient SIP restructuring.
          </p>
          <div className="mt-6 flex justify-center gap-4">
            <Link
              to="/contact"
              className="px-8 py-3 bg-[#16325B] hover:bg-[#1D4ED8] text-white font-bold rounded-xl transition-all shadow-lg shadow-[#16325B]/30 border border-blue-400/20"
            >
              Schedule Advisory Call
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MutualFunds;